import { z } from "zod";

/** Schema for the kind of node in the drive browser. */
export const DriveEntryKindSchema = z.enum(["site", "drive", "folder"]);

/** The kind of node in the drive browser. */
export type DriveEntryKind = z.infer<typeof DriveEntryKindSchema>;

/** A site, drive, or folder shown in the drive browser. */
export const DriveEntrySchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: DriveEntryKindSchema,
  driveId: z.string().nullable(),
  webUrl: z.string().nullable(),
  hasChildren: z.boolean(),
});

/** A site, drive, or folder shown in the drive browser. */
export type DriveEntry = z.infer<typeof DriveEntrySchema>;

/** Response for GET /connections/:connectionId/drive/entries. */
export const DriveEntriesResponseSchema = z.object({
  entries: z.array(DriveEntrySchema),
  nextPageToken: z.string().nullable(),
});

/** Response for GET /connections/:connectionId/drive/entries. */
export type DriveEntriesResponse = z.infer<typeof DriveEntriesResponseSchema>;

const PageTokenSchema = z.string().optional();

/** Query for GET /connections/:connectionId/drive/entries; omit `parentKind` to list the roots. */
export const DriveEntriesQuerySchema = z.union([
  z.discriminatedUnion("parentKind", [
    z.object({
      parentKind: z.literal("site"),
      parentId: z.string().min(1),
      pageToken: PageTokenSchema,
    }),
    z.object({
      parentKind: z.literal("drive"),
      driveId: z.string().min(1),
      pageToken: PageTokenSchema,
    }),
    z.object({
      parentKind: z.literal("folder"),
      driveId: z.string().min(1),
      parentId: z.string().min(1),
      pageToken: PageTokenSchema,
    }),
  ]),
  z.object({
    parentKind: z.undefined().optional(),
    pageToken: PageTokenSchema,
  }),
]);

/** Query for GET /connections/:connectionId/drive/entries; omit `parentKind` to list the roots. */
export type DriveEntriesQuery = z.infer<typeof DriveEntriesQuerySchema>;
